import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { Step } from "~/components/build/Show/lib/types";
import { BuildSearchParams } from "~/components/build/Show/lib/searchParams";

export const DRAWER_PARAMS = ["jid", "sid", "open", "tab"] as const;

/**
 * Returns a copy of the given search params with all drawer-specific params removed.
 */
export function withoutDrawerParams(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(searchParams);
  DRAWER_PARAMS.forEach((param) => params.delete(param));
  return params;
}

export function urlForView(buildPath: string, view: BuildView, params?: BuildSearchParams): string {
  const views = {
    [BuildView.Summary]: "summary",
    [BuildView.Steps]: "steps",
    [BuildView.Canvas]: "steps/canvas",
    [BuildView.Table]: "steps/table",
    [BuildView.List]: "steps/list",
    [BuildView.Waterfall]: "steps/waterfall",
    [BuildView.Tests]: "tests",
    [BuildView.Annotations]: "annotations",
  };

  if (!views[view]) {
    throw new Error(`Unknown view: ${view}`);
  }

  const pathname = `${buildPath}/${views[view]}`;

  if (params) {
    return applySearchParams(pathname, params);
  }

  return pathname;
}

export function applySearchParams(path: string, params: BuildSearchParams, base?: URLSearchParams) {
  const searchParams = base ? new URLSearchParams(base) : new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      searchParams.set(key, value);
    }
  }

  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Build a URL that sets `newParams` as the drawer params while preserving
 * any non-drawer params already in `currentSearch` (e.g. `annotations_tab`).
 */
export function preserveNonDrawerParams(
  path: string,
  currentSearch: string | URLSearchParams,
  newParams: BuildSearchParams,
): string {
  const base = withoutDrawerParams(
    typeof currentSearch === "string" ? new URLSearchParams(currentSearch) : currentSearch,
  );
  return applySearchParams(path, newParams, base);
}

/**
 * Resolve a job URL based on the current location or a custom pathname.
 *
 * @param jobUuid - The UUID of the job to resolve.
 * @param options - Options for resolving the URL.
 * @param options.pathname - The base path to use for the URL. Defaults to the current window location.
 * @param options.open - Whether to open the drawer for the job. Defaults to true.
 */
export function resolveJobUrl(
  jobUuid: string,
  { pathname = window.location.pathname, open = true } = {},
) {
  return applySearchParams(pathname, {
    jid: jobUuid,
    open: open === false ? "false" : undefined,
  });
}

/**
 * Resolve a step URL based on the current location or a custom pathname.
 *
 * @param stepUuid - The UUID of the step to resolve.
 * @param options - Options for resolving the URL.
 * @param options.pathname - The base path to use for the URL. Defaults to the current window location.
 * @param options.open - Whether to open the drawer for the step. Defaults to true.
 */
export function resolveStepUrl(
  stepUuid: Step["uuid"],
  { pathname = window.location.pathname, open = true } = {},
) {
  return applySearchParams(pathname, {
    sid: stepUuid,
    open: open === false ? "false" : undefined,
  });
}
